import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, ArrowLeft, Calculator } from "lucide-react";
import { toast } from "sonner";
import { listScreenTypes, previewQuote, saveQuote } from "@/lib/api";

const emptyRoom = () => ({
  id: crypto.randomUUID(),
  name: "",
  width_mm: 0,
  height_mm: 0,
  screen_type: null,
  area_sqm: 0,
  price: 0,
  matched_tier_mm: null,
  oversized: false,
  cut_pieces: [],
});

// Tier-based price lookup matching backend logic
const priceFromTiers = (widthMm, tiers) => {
  if (!tiers || tiers.length === 0) return { price: 0, matched: null, oversized: false };
  const sorted = [...tiers].sort((a, b) => Number(a.width_mm) - Number(b.width_mm));
  for (const t of sorted) {
    if (widthMm <= Number(t.width_mm)) {
      return { price: Number(t.price), matched: Number(t.width_mm), oversized: false };
    }
  }
  const last = sorted[sorted.length - 1];
  return { price: Number(last.price), matched: Number(last.width_mm), oversized: true };
};

const computeCutPieces = (recipe, w, h) => {
  if (!recipe || recipe.length === 0 || (w <= 0 && h <= 0)) return [];
  return recipe
    .map((p) => {
      let base = w;
      if (p.source === "height") base = h;
      else if (p.source === "width/2") base = w / 2;
      else if (p.source === "height/2") base = h / 2;
      const length = base + (Number(p.offset_mm) || 0);
      return {
        label: p.label,
        qty: Math.max(1, Number(p.qty) || 1),
        length_mm: Number(length.toFixed(1)),
      };
    })
    .filter((p) => p.length_mm > 0);
};

const computeRoom = (r, tierMap, recipeMap) => {
  const w = Number(r.width_mm) || 0;
  const h = Number(r.height_mm) || 0;
  const area_sqm = Number(((w / 1000) * (h / 1000)).toFixed(4));
  const tiers = r.screen_type ? tierMap[r.screen_type] || [] : [];
  const { price, matched, oversized } = priceFromTiers(w, tiers);
  const cut_pieces = r.screen_type
    ? computeCutPieces(recipeMap[r.screen_type] || [], w, h)
    : [];
  return {
    ...r,
    area_sqm,
    price: Number(price.toFixed(2)),
    matched_tier_mm: matched,
    oversized,
    cut_pieces,
  };
};

const Review = () => {
  const navigate = useNavigate();
  const [quote, setQuote] = useState({
    client_name: "",
    client_phone: "",
    client_address: "",
    color: "",
    area: "",
    rooms: [],
    photos: [],
    total: 0,
  });
  const [screenTypes, setScreenTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("pending_quote");
    if (!raw) {
      navigate("/");
      return;
    }
    const parsed = JSON.parse(raw);
    if (!parsed.photos && parsed.photo_data_url) {
      parsed.photos = [parsed.photo_data_url];
    }
    parsed.photos = parsed.photos || [];
    if (!parsed.rooms || parsed.rooms.length === 0) {
      parsed.rooms = [emptyRoom()];
    } else {
      parsed.rooms = parsed.rooms.map((r) => ({ ...emptyRoom(), ...r }));
    }
    setQuote(parsed);
    listScreenTypes()
      .then(setScreenTypes)
      .catch(() => toast.error("Failed loading screen types"));
  }, [navigate]);

  const tierMap = useMemo(() => {
    const m = {};
    screenTypes.forEach((st) => (m[st.key] = st.tiers || []));
    return m;
  }, [screenTypes]);

  const recipeMap = useMemo(() => {
    const m = {};
    screenTypes.forEach((st) => (m[st.key] = st.cut_pieces || []));
    return m;
  }, [screenTypes]);

  // Recalc whenever tierMap or recipeMap changes
  useEffect(() => {
    setQuote((prev) => {
      let total = 0;
      const rooms = prev.rooms.map((r) => {
        const updated = computeRoom(r, tierMap, recipeMap);
        total += updated.price;
        return updated;
      });
      return { ...prev, rooms, total: Number(total.toFixed(2)) };
    });
  }, [tierMap, recipeMap]);

  const updateRoom = (id, patch) => {
    setQuote((prev) => {
      let total = 0;
      const rooms = prev.rooms.map((r) => {
        if (r.id !== id) {
          total += r.price || 0;
          return r;
        }
        const updated = computeRoom({ ...r, ...patch }, tierMap, recipeMap);
        total += updated.price;
        return updated;
      });
      return { ...prev, rooms, total: Number(total.toFixed(2)) };
    });
  };

  const addRoom = () => {
    setQuote((p) => ({ ...p, rooms: [...p.rooms, emptyRoom()] }));
  };

  const removeRoom = (id) => {
    setQuote((p) => ({ ...p, rooms: p.rooms.filter((r) => r.id !== id) }));
  };

  const applyAllType = (key) => {
    setQuote((prev) => {
      let total = 0;
      const rooms = prev.rooms.map((r) => {
        const updated = computeRoom(
          { ...r, screen_type: key },
          tierMap,
          recipeMap
        );
        total += updated.price;
        return updated;
      });
      return { ...prev, rooms, total: Number(total.toFixed(2)) };
    });
  };

  const save = async () => {
    // basic validation
    const validRooms = quote.rooms.filter(
      (r) => r.name && r.width_mm > 0 && r.height_mm > 0
    );
    if (validRooms.length === 0) {
      toast.error("Add at least one room with name, width, and height");
      return;
    }
    if (!quote.client_name) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_name: quote.client_name,
        client_phone: (quote.client_phone || "").trim(),
        client_address: (quote.client_address || "").trim(),
        color: quote.color,
        area: quote.area,
        rooms: validRooms,
        photos: quote.photos || [],
      };
      const saved = await saveQuote(payload);
      toast.success("Quote saved");
      sessionStorage.removeItem("pending_quote");
      navigate(`/quotes/${saved.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <button
        onClick={() => navigate("/")}
        className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-2 mb-4"
        data-testid="back-btn"
      >
        <ArrowLeft size={14} /> Back to upload
      </button>

      <div className="mb-6">
        <div className="label-xs">Module 02 / Review</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          REVIEW & PRICE
        </h1>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        {/* LEFT: Photo + Client Info */}
        <div className="space-y-6">
          <div className="border border-zinc-200">
            <div className="px-4 py-3 border-b border-zinc-200 label-xs bg-[#f4f4f5] flex items-center justify-between">
              <span>Source {quote.photos.length > 1 ? `Pages · ${quote.photos.length}` : "Photo"}</span>
            </div>
            {quote.photos.length > 0 ? (
              <div className="max-h-[600px] overflow-y-auto divide-y divide-zinc-200">
                {quote.photos.map((src, i) => (
                  <div key={i} className="relative">
                    {quote.photos.length > 1 && (
                      <div className="absolute top-2 left-2 bg-[#09090b] text-white label-xs !text-white px-2 py-1 z-10">
                        Page {i + 1}
                      </div>
                    )}
                    <img
                      src={src}
                      alt={`sketch-${i}`}
                      className="w-full h-auto"
                      data-testid={`source-photo-${i}`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-10 text-center text-xs text-zinc-500 font-mono">
                No photo attached (manual entry)
              </div>
            )}
          </div>

          <div className="border border-zinc-200">
            <div className="px-4 py-3 border-b border-zinc-200 label-xs bg-[#f4f4f5]">
              Client Info
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="label-xs mb-1">Client name *</div>
                <input
                  data-testid="input-client-name"
                  className="input-sharp"
                  value={quote.client_name}
                  onChange={(e) =>
                    setQuote((p) => ({ ...p, client_name: e.target.value }))
                  }
                  placeholder="e.g. Lupe"
                />
              </div>
              <div>
                <div className="label-xs mb-1">
                  Phone (WhatsApp, with country code)
                </div>
                <input
                  data-testid="input-client-phone"
                  className="input-sharp tabular-nums"
                  value={quote.client_phone || ""}
                  onChange={(e) =>
                    setQuote((p) => ({ ...p, client_phone: e.target.value }))
                  }
                  placeholder="e.g. +34612345678"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>
              <div className="sm:col-span-2">
                <div className="label-xs mb-1">Address</div>
                <input
                  data-testid="input-client-address"
                  className="input-sharp"
                  value={quote.client_address || ""}
                  onChange={(e) =>
                    setQuote((p) => ({ ...p, client_address: e.target.value }))
                  }
                  placeholder="e.g. Bel Air, C/Retiro 4, apt 412"
                  autoComplete="street-address"
                />
              </div>
              <div>
                <div className="label-xs mb-1">Color</div>
                <input
                  data-testid="input-color"
                  className="input-sharp"
                  value={quote.color}
                  onChange={(e) =>
                    setQuote((p) => ({ ...p, color: e.target.value }))
                  }
                  placeholder="e.g. White"
                />
              </div>
              <div>
                <div className="label-xs mb-1">Area</div>
                <input
                  data-testid="input-area"
                  className="input-sharp"
                  value={quote.area}
                  onChange={(e) =>
                    setQuote((p) => ({ ...p, area: e.target.value }))
                  }
                  placeholder="e.g. La Cala Hills"
                />
              </div>
            </div>
          </div>

          <div className="border border-zinc-200">
            <div className="px-4 py-3 border-b border-zinc-200 label-xs bg-[#f4f4f5]">
              Bulk Assign Screen Type
            </div>
            <div className="p-4">
              <select
                data-testid="bulk-type-select"
                className="input-sharp"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) applyAllType(e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="" disabled>
                  Apply type to all rooms...
                </option>
                {screenTypes.map((st) => (
                  <option key={st.key} value={st.key}>
                    {st.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* RIGHT: Table + Total */}
        <div>
          <div className="border border-zinc-200">
            <div className="px-4 py-3 border-b border-zinc-200 label-xs bg-[#f4f4f5] flex justify-between items-center">
              <span>Rooms · {quote.rooms.length}</span>
              <button
                onClick={addRoom}
                data-testid="add-room-btn"
                className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#005bea] hover:text-[#0047c2] inline-flex items-center gap-1"
              >
                <Plus size={12} /> Add row
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono" data-testid="rooms-table">
                <thead className="bg-white">
                  <tr className="text-left">
                    <th className="px-3 py-2 border-b border-zinc-200 label-xs w-[18%]">Room</th>
                    <th className="px-3 py-2 border-b border-zinc-200 label-xs w-[10%]">W (mm)</th>
                    <th className="px-3 py-2 border-b border-zinc-200 label-xs w-[10%]">H (mm)</th>
                    <th className="px-3 py-2 border-b border-zinc-200 label-xs w-[8%]">m²</th>
                    <th className="px-3 py-2 border-b border-zinc-200 label-xs w-[17%]">Screen Type</th>
                    <th className="px-3 py-2 border-b border-zinc-200 label-xs w-[17%]">Cut Pieces</th>
                    <th className="px-3 py-2 border-b border-zinc-200 label-xs text-right w-[13%]">Price</th>
                    <th className="px-3 py-2 border-b border-zinc-200 w-[7%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {quote.rooms.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="border-b border-zinc-100 hover:bg-[#fafafa]"
                      data-testid={`room-row-${idx}`}
                    >
                      <td className="px-2 py-1">
                        <input
                          className="input-sharp !py-1.5 !px-2 font-display !text-lg font-bold tracking-tight"
                          value={r.name}
                          onChange={(e) => updateRoom(r.id, { name: e.target.value })}
                          placeholder="Lounge"
                          data-testid={`room-name-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          className="input-sharp !py-1.5 !px-2 text-right"
                          value={r.width_mm || ""}
                          onChange={(e) =>
                            updateRoom(r.id, { width_mm: Number(e.target.value) })
                          }
                          data-testid={`room-width-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1">
                        <input
                          type="number"
                          className="input-sharp !py-1.5 !px-2 text-right"
                          value={r.height_mm || ""}
                          onChange={(e) =>
                            updateRoom(r.id, { height_mm: Number(e.target.value) })
                          }
                          data-testid={`room-height-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-2 text-zinc-600">
                        {r.area_sqm.toFixed(3)}
                      </td>
                      <td className="px-2 py-1">
                        <select
                          className="input-sharp !py-1.5 !px-2"
                          value={r.screen_type || ""}
                          onChange={(e) =>
                            updateRoom(r.id, {
                              screen_type: e.target.value || null,
                            })
                          }
                          data-testid={`room-type-${idx}`}
                        >
                          <option value="">— select —</option>
                          {screenTypes.map((st) => (
                            <option key={st.key} value={st.key}>
                              {st.label}
                              {st.tiers && st.tiers.length > 0
                                ? ` · ${st.tiers.length} tier(s)`
                                : " · no tiers"}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px] text-zinc-700 tabular-nums leading-tight">
                        {r.cut_pieces && r.cut_pieces.length > 0 ? (
                          <div data-testid={`room-cut-${idx}`} className="space-y-0.5">
                            {r.cut_pieces.map((p, pi) => (
                              <div key={pi}>
                                <span className="font-semibold">
                                  {p.qty}/
                                </span>{" "}
                                {p.length_mm}
                                <span className="text-zinc-400"> · {p.label}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-zinc-400">—</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        <div className="font-semibold">
                          €{r.price.toFixed(2)}
                        </div>
                        {r.matched_tier_mm != null && (
                          <div
                            className={`text-[10px] mt-0.5 ${
                              r.oversized ? "text-[#ff5722] font-bold" : "text-zinc-500"
                            }`}
                            data-testid={`room-tier-${idx}`}
                          >
                            {r.oversized
                              ? `OVER · using ${r.matched_tier_mm}mm`
                              : `${r.matched_tier_mm}mm tier`}
                          </div>
                        )}
                        {r.screen_type && r.matched_tier_mm == null && (
                          <div className="text-[10px] mt-0.5 text-[#ff5722]">
                            no tiers set
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <button
                          onClick={() => removeRoom(r.id)}
                          className="text-zinc-400 hover:text-[#ff5722]"
                          data-testid={`room-delete-${idx}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Total block */}
          <div className="mt-6 border border-[#09090b] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="label-xs">Total Quote</div>
              <div
                className="font-display text-5xl sm:text-6xl font-black tracking-tighter tabular-nums mt-1"
                data-testid="total-price"
              >
                €{quote.total.toFixed(2)}
              </div>
              <div className="label-xs mt-2 flex items-center gap-2">
                <Calculator size={11} /> Σ {quote.rooms.length} rooms
              </div>
            </div>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-quote-btn"
              className="btn-sharp primary"
            >
              <Save size={14} /> {saving ? "Saving..." : "Save Quote"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Review;
